import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  faSignOut,
  faUserEdit,
  faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { createBemBlockBuilder, Popover } from '@ebsco-ui/ebsco-ui';

import { FeatureFlagDisplay, Icon } from '@app/components';
import { useAppContext } from '@app/contexts';
import { sharedMessages } from '@app/translations';
import { Feature, Route } from '@app/constants';

import { AccountIcon } from '../AccountIcon';

import './AccountInfo.scss';

const cnBem = createBemBlockBuilder(['accountInfo']);

export const AccountInfo: FC = () => {
  const { $t } = useIntl();
  const { patronSession, isLoggedIn } = useAppContext();

  const getUserInfo = () => {
    if (!(patronSession && isLoggedIn)) {
      return null;
    }

    return (
      <FeatureFlagDisplay
        flagToMatch={{ name: Feature.consortiaLoginFlow }}
        renderOn={() => (
          <div className={cnBem('__username')} data-auto="user-name">
            {patronSession.user?.externalSystemId}
          </div>
        )}
        renderOff={() => (
          <>
            <div className={cnBem('__username')} data-auto="user-name">
              {patronSession.user?.firstName} {patronSession.user?.lastName}
            </div>
            <div className={cnBem('__userEmail')} data-auto="user-email">
              {patronSession.user?.email}
            </div>
          </>
        )}
      />
    );
  };

  return (
    <Popover
      uniqueName="account-info"
      panelPlacement="bottom-end"
      className={cnBem('__wrapper')}
    >
      <Popover.ContextConsumer>
        {({ closePopover }) => (
          <>
            <Popover.Button
              styleType="flat-icon"
              id="account-info-button"
              className="popoverButton"
              aria-label={$t({
                id: 'navigation.openUserMenu',
                defaultMessage: 'Open user info',
              })}
            >
              <AccountIcon />
            </Popover.Button>
            <Popover.Panel
              aria-describedby="account-info-button"
              className="popoverWrapper"
            >
              <h2 className={cnBem('__header')}>
                {isLoggedIn ? (
                  <FormattedMessage
                    id="navigation.myAccount"
                    defaultMessage="My account"
                  />
                ) : (
                  <FormattedMessage
                    id="navigation.libraryUser"
                    defaultMessage="Library user"
                  />
                )}
              </h2>
              <div className={cnBem('__userInfoWrapper')}>
                <AccountIcon className={cnBem('__userIcon')} />
                {patronSession && isLoggedIn && (
                  <div className={cnBem('__userInfo')}>{getUserInfo()}</div>
                )}
              </div>
              <ul className={cnBem('__links')}>
                <FeatureFlagDisplay
                  flagToMatch={{ name: Feature.consortiaLoginFlow }}
                  renderOff={() => (
                    <li>
                      <Icon icon={faUserEdit} className={cnBem('__linkIcon')}>
                        <FormattedMessage {...sharedMessages.preferences}>
                          {message => (
                            <Link to={Route.preferences} onClick={closePopover}>
                              {message[0]}
                            </Link>
                          )}
                        </FormattedMessage>
                      </Icon>
                    </li>
                  )}
                />
                <li>
                  <Icon icon={faSignOut} className={cnBem('__linkIcon')}>
                    {isLoggedIn ? (
                      <FormattedMessage
                        id="navigation.signOut"
                        defaultMessage="Sign out"
                      >
                        {message => <Link to={Route.logout}>{message[0]}</Link>}
                      </FormattedMessage>
                    ) : (
                      <FormattedMessage {...sharedMessages.signIn}>
                        {message => <Link to={Route.login}>{message[0]}</Link>}
                      </FormattedMessage>
                    )}
                  </Icon>
                </li>
                {!isLoggedIn && (
                  <FeatureFlagDisplay
                    flagToMatch={{ name: Feature.preregistration }}
                  >
                    <li>
                      <Icon icon={faUserPlus} className={cnBem('__linkIcon')}>
                        <FormattedMessage
                          id="navigation.createAccount"
                          defaultMessage="Create account"
                        >
                          {message => (
                            <Link to={Route.createAccount}>{message[0]}</Link>
                          )}
                        </FormattedMessage>
                      </Icon>
                    </li>
                  </FeatureFlagDisplay>
                )}
              </ul>
            </Popover.Panel>
          </>
        )}
      </Popover.ContextConsumer>
    </Popover>
  );
};
